import { Controller } from "@hotwired/stimulus"

/**
 * Live Viewer Controller
 *
 * Handles HLS video playback for live streams and VOD content.
 * Uses HLS.js for cross-browser HLS support.
 *
 * Usage:
 * <div data-controller="live-viewer"
 *      data-live-viewer-hls-url-value="https://live.mipadel.net/mipadel/streams/abc123.m3u8"
 *      data-live-viewer-is-live-value="true"
 *      data-live-viewer-stream-id-value="abc123">
 */
export default class extends Controller {
  static targets = ["video", "loading", "error"]

  static values = {
    hlsUrl: String,
    isLive: Boolean,
    streamId: String
  }

  connect() {
    this.hls = null
    this.retryCount = 0
    this.maxRetries = 5
    this.retryDelay = 3000

    if (this.hlsUrlValue) {
      this._initializePlayer()
    }
  }

  disconnect() {
    this._cleanup()
  }

  // Private methods

  _initializePlayer() {
    const video = this.videoTarget

    // Check for native HLS support (Safari, iOS)
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      this._initializeNativeHLS(video)
    } else if (typeof Hls !== "undefined" && Hls.isSupported()) {
      this._initializeHLSJS(video)
    } else {
      this._showError("Votre navigateur ne supporte pas la lecture vidéo HLS")
    }
  }

  _initializeNativeHLS(video) {
    video.src = this.hlsUrlValue

    video.addEventListener("loadedmetadata", () => {
      this._hideLoading()
      video.play().catch(err => {
        // Autoplay blocked - that's ok, user can click play
        console.log("Autoplay prevented:", err)
      })
    })

    video.addEventListener("error", () => {
      this._handlePlaybackError(video.error)
    })
  }

  _initializeHLSJS(video) {
    this.hls = new Hls({
      // Configuration optimisée pour le live
      enableWorker: true,
      lowLatencyMode: this.isLiveValue,
      backBufferLength: this.isLiveValue ? 30 : 90,
      maxBufferLength: this.isLiveValue ? 10 : 30,
      maxMaxBufferLength: this.isLiveValue ? 30 : 120,
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 6,
      // Retry configuration
      manifestLoadingMaxRetry: 4,
      manifestLoadingRetryDelay: 1000,
      levelLoadingMaxRetry: 4,
      levelLoadingRetryDelay: 1000,
      fragLoadingMaxRetry: 6,
      fragLoadingRetryDelay: 1000,
    })

    this.hls.loadSource(this.hlsUrlValue)
    this.hls.attachMedia(video)

    this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
      this._hideLoading()
      video.play().catch(err => {
        console.log("Autoplay prevented:", err)
      })
    })

    this.hls.on(Hls.Events.ERROR, (event, data) => {
      this._handleHLSError(data)
    })

    // Quality level info
    this.hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      const level = this.hls.levels[data.level]
      if (level) {
        console.log(`Quality: ${level.height}p @ ${Math.round(level.bitrate / 1000)}kbps`)
      }
    })
  }

  _handleHLSError(data) {
    console.error("HLS Error:", data.type, data.details)

    if (data.fatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // Try to recover network error
          if (this.retryCount < this.maxRetries) {
            this.retryCount++
            console.log(`Network error, retrying (${this.retryCount}/${this.maxRetries})...`)
            setTimeout(() => {
              this.hls.startLoad()
            }, this.retryDelay)
          } else {
            this._showError("Impossible de charger le stream. Vérifiez votre connexion.")
          }
          break

        case Hls.ErrorTypes.MEDIA_ERROR:
          // Try to recover media error
          console.log("Media error, attempting recovery...")
          this.hls.recoverMediaError()
          break

        default:
          // Cannot recover
          this._showError("Erreur de lecture. Rechargez la page.")
          this._cleanup()
          break
      }
    }
  }

  _handlePlaybackError(error) {
    console.error("Playback error:", error)

    let message = "Erreur de lecture vidéo"
    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          message = "Lecture interrompue"
          break
        case MediaError.MEDIA_ERR_NETWORK:
          message = "Erreur réseau"
          break
        case MediaError.MEDIA_ERR_DECODE:
          message = "Erreur de décodage"
          break
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message = "Format non supporté"
          break
      }
    }

    this._showError(message)
  }

  _showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
    this._hideLoading()
  }

  _hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  _cleanup() {
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }
}
